import {
    AfterViewInit,
    Component,
    DestroyRef,
    ElementRef,
    HostListener,
    inject,
    Inject,
    PLATFORM_ID,
    ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { from, map, Observable, Subscription } from 'rxjs';
import { chain } from 'underscore';
import { TranslateService } from '@jht/brand-translate';

import { NavigationService } from './navigation.service';
import { Config } from '../../../shared/config';
import { ContentService } from '@src/app/shared/content/content.service';
import { ScriptLoaderService } from '../../../shared/services/script-loader.service';
import { SubNavigation, TopLevelNavItem } from './models';

@Component({
    selector: 'vision2-navigation',
    templateUrl: './navigation.component.html',
    styleUrls: ['./navigation.component.scss'],
    standalone: false,
})
export class NavigationComponent implements AfterViewInit {
    document = inject(DOCUMENT);
    scriptLoader = inject(ScriptLoaderService);
    componentData$!: Observable<TopLevelNavItem[]>;
    collapse: boolean = false;
    activeDropdown: boolean[] = [];

    navPanelChanges$!: Observable<void>;
    navPanelSubscription!: Subscription;
    topNavItemColumns: { [key: number]: string[] } = {};
    topNavItemsByColumn: { [key: number]: Record<string, SubNavigation[]> } =
        {};
    isReady: boolean = false;

    private mutationObserver!: MutationObserver;
    searchInput = false;
    searchText: string = '';
    isMobileMenuVisible = false;

    dropdownTriggeredByClick: boolean = false;

    @ViewChild('topNav') topNav!: ElementRef;
    @ViewChild('strengthNav') strengthNav!: ElementRef;
    @ViewChild('navPanel') navPanel!: ElementRef<HTMLElement>;

    @HostListener('document:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            this.closeCollapse();
        }
    }

    @HostListener('document:click', ['$event']) onDocumentClick(event: Event) {
        if (!this.topNav?.nativeElement.contains(event.target)) {
            this.closeCollapse();
        }
    }

    private config = inject(Config);
    private platformId = inject(PLATFORM_ID);
    private contentService = inject(ContentService);
    private navService = inject(NavigationService);
    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);
    private destroyRef = inject(DestroyRef);
    private translateService = inject(TranslateService);

    constructor() {}

    ngAfterViewInit(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        const navPanel = this.navPanel.nativeElement;

        this.mutationObserver = new MutationObserver(() =>
            this.navService.handleNavPanelChanges(navPanel),
        );

        this.mutationObserver.observe(navPanel, {
            attributes: true,
            attributeFilter: ['class'],
        });

        this.navPanelChanges$ = new Observable<void>((observer) => {
            return () => {
                this.mutationObserver.disconnect();
            };
        });
        this.isReady = true;
    }

    ngOnInit() {
        this.initializeTopLevelNavigation();

        const topNavDownIcon = this.translateService.instant(
            'NAVIGATION_TOP-LEVEL_DOWN-CARET-ICON_IMAGE_URL',
        );
        const topNavUpIcon = this.translateService.instant(
            'NAVIGATION_TOP-LEVEL_UP-CARET-ICON_IMAGE_URL',
        );

        this.document.documentElement.style.setProperty(
            '--top-nav-icon',
            `url('${topNavDownIcon}')`,
        );
        this.document.documentElement.style.setProperty(
            '--top-nav-icon-up',
            `url('${topNavUpIcon}')`,
        );

        this.activatedRoute.queryParams
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((queryParams) => {
                if (queryParams['q']) {
                    this.searchText = queryParams['q'];
                }
            });
    }

    search(searchText: string) {
        this.router.navigate(
            [this.config.getLanguageRoutePrefix() + '/', 'search'],
            { queryParams: { q: searchText } },
        );
    }

    ngOnDestroy() {
        this.navPanelSubscription?.unsubscribe();

        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
        }
    }

    private initializeTopLevelNavigation() {
        if (isPlatformBrowser(this.platformId)) {
            this.componentData$ = from(
                this.navService.load(this.contentService),
            ).pipe(
                map(() => {
                    const topLevelNav = this.navService.topLevel || [];
                    const dropDowns = this.navService.dropDowns || [];
                    const groupedDropDowns = chain(dropDowns)
                        .groupBy('category')
                        .value();
                    const groupedCategories = Object.keys(groupedDropDowns).map(
                        (key) => ({
                            category: key,
                            products: groupedDropDowns[key],
                        }),
                    );

                    this.assignNavItems(topLevelNav, groupedCategories);

                    topLevelNav?.forEach((navItem, index) => {
                        if (navItem.navItems && navItem.navItems.length > 0) {
                            this.topNavItemColumns[index] =
                                this.getUniqueColumns(navItem.navItems);
                            this.topNavItemsByColumn[index] =
                                this.groupNavItemsByColumn(navItem.navItems);
                        }
                    });

                    return topLevelNav || [];
                }),
            );
        }
    }

    getUniqueColumns(navItems: SubNavigation[]): string[] {
        if (!navItems || navItems.length === 0) {
            return [];
        }

        const uniqueColumns = [...new Set(navItems.map((item) => item.column))];
        const validColumns = uniqueColumns.filter(
            (column): column is string => !!column,
        );
        const columnOrder: Record<string, number> = {
            one: 1,
            two: 2,
            three: 3,
            four: 4,
        };

        return validColumns.sort(
            (a, b) => (columnOrder[a] || 999) - (columnOrder[b] || 999),
        );
    }

    private groupNavItemsByColumn(
        navItems: SubNavigation[],
    ): Record<string, SubNavigation[]> {
        return navItems.reduce((acc, item) => {
            const column = item.column;
            if (!column) {
                return acc;
            }

            if (!acc[column]) {
                acc[column] = [];
            }

            acc[column].push(item);
            return acc;
        }, {} as Record<string, SubNavigation[]>);
    }

    private assignNavItems(
        topLevelNav: TopLevelNavItem[],
        groupedCategories: Array<{
            category: string;
            products: SubNavigation[];
        }>,
    ) {
        topLevelNav.forEach((navItem: TopLevelNavItem) => {
            const matchingCategory = groupedCategories.find(
                (category: { category: string; products: SubNavigation[] }) =>
                    category.category === navItem['dropdown-id'],
            );

            if (matchingCategory) {
                navItem['navItems'] = matchingCategory.products;
            }
        });

        return topLevelNav;
    }

    onClickDropdown(dropdownNumber: number): void {
        for (let i = 0; i < this.activeDropdown.length; i++) {
            if (i !== dropdownNumber) {
                this.activeDropdown[i] = false;
            }
        }

        this.activeDropdown[dropdownNumber] =
            !this.activeDropdown[dropdownNumber];

        this.collapse = this.activeDropdown.some((isActive) => isActive);
    }

    closeCollapse($event?: Event) {
        this.activeDropdown.fill(false);
        if ($event) {
            this.topNav.nativeElement.style.display = 'none';
        }
        this.collapse = false;
    }

    toggleSearch() {
        this.searchInput = !this.searchInput;
        this.isMobileMenuVisible = false;
    }
}
